/**
 * Small SVG marks shared by the charts: point markers in several shapes, and coordinate
 * rounding.
 *
 * Marker shapes differ by series as well as colour, so a line graph or scatter plot
 * still reads when it is printed in grayscale or seen with a colour-vision deficiency —
 * APA 7 asks for exactly that redundancy in figures.
 */

import { CHART_INK } from "@/lib/tool-kit/palette";

/** Round a coordinate to 0.1 unit — exact enough to draw, short enough to ship. */
export function px(value: number): number {
  return Math.round(value * 10) / 10;
}

export const MARKER_SHAPES = [
  "circle",
  "square",
  "triangle",
  "diamond",
  "triangleDown",
] as const;

export type MarkerShape = (typeof MARKER_SHAPES)[number];

export function markerShape(index: number): MarkerShape {
  return MARKER_SHAPES[index % MARKER_SHAPES.length];
}

function polygon(points: [number, number][]): string {
  return points.map(([x, y]) => `${px(x)},${px(y)}`).join(" ");
}

/**
 * One point marker centred on (x, y). `size` is the marker's width; a surface-coloured
 * ring keeps overlapping markers and markers sitting on a line distinguishable.
 */
export function Marker({
  shape,
  x,
  y,
  size = 9,
  fill,
  hollow = false,
}: {
  shape: MarkerShape;
  x: number;
  y: number;
  size?: number;
  fill: string;
  /** Draw an outline only — for outliers, which are points, not data mass. */
  hollow?: boolean;
}) {
  const half = size / 2;
  const paint = hollow
    ? { fill: CHART_INK.background, stroke: fill, strokeWidth: 1.5 }
    : { fill, stroke: CHART_INK.background, strokeWidth: 1.25 };
  if (shape === "square") {
    return (
      <rect
        height={px(size * 0.9)}
        width={px(size * 0.9)}
        x={px(x - half * 0.9)}
        y={px(y - half * 0.9)}
        {...paint}
      />
    );
  }
  if (shape === "triangle" || shape === "triangleDown") {
    const flip = shape === "triangle" ? 1 : -1;
    return (
      <polygon
        points={polygon([
          [x, y - half * 1.1 * flip],
          [x + half * 1.05, y + half * 0.8 * flip],
          [x - half * 1.05, y + half * 0.8 * flip],
        ])}
        {...paint}
      />
    );
  }
  if (shape === "diamond") {
    return (
      <polygon
        points={polygon([
          [x, y - half * 1.15],
          [x + half * 1.15, y],
          [x, y + half * 1.15],
          [x - half * 1.15, y],
        ])}
        {...paint}
      />
    );
  }
  return <circle cx={px(x)} cy={px(y)} r={px(half)} {...paint} />;
}
